/**
 * FRLandmarks — геометрия лица для ретуши: контур, глаза, рот, брови.
 *
 * ЗАЧЕМ ЭТО ОТДЕЛЬНЫМ СЛОЕМ. Ретушь без геометрии не работает — проверено
 * замером на этом же корпусе: маска кожи «по цвету» (яркий-красноватый пиксель
 * = кожа) захватывает фон, джинсы и стену, и алгоритм начинает выедать
 * шумовые пятна по всему кадру вместо бликов на лице. Границу «где кожа»
 * должна задавать ГЕОМЕТРИЯ, а не оттенок.
 *
 * И вторая, менее очевидная причина: глаза и зубы — это ЯРКИЕ и
 * ОБЕСЦВЕЧЕННЫЕ пиксели, то есть ровно то, что любой детектор блика считает
 * бликом. Без исключения этих областей «убрать блеск» гасит блеск в глазах и
 * делает зубы серыми. Индустриальный эталон (Beauty Box 6.0) вынес это в
 * отдельную возможность и честно пишет, что она замедляет работу; у нас она
 * встроена, потому что без неё результат просто неверен.
 *
 * Публичный интерфейс — window.FRLandmarks:
 *   ensure(sources, {numFaces, mode})   → Promise<detector>
 *   detect(source, w, h, {timestamp})   → [{oval, leftEye, rightEye, lips,
 *                                          brows, box, pts}]  (0..1 координаты)
 *   close()                             → освободить детектор
 *
 * Источники передаёт страница (blade-хелпер `cdn()` не работает в статике) —
 * тот же приём, что у subject-tracker.js.
 */
(function (root) {
    'use strict';

    var _det = null, _ready = null, _mode = null, _mp = null, _ts = 0, _source = null;
    // Лестница кандидатов и её память. Кандидат = источник файлов × делегат.
    // `_dead` помнит тех, кто упал НЕ по сети (проба или работа): их не
    // пробуют повторно в этой вкладке — упавший wasm-модуль не оживает.
    // Сетевые отказы (ESM/модель не скачались) в `_dead` не попадают: канал
    // может вернуться, и следующий вызов имеет право попробовать снова.
    var _cand = null, _sources = null, _opts = null, _dead = {};
    // Отдельно — мёртвые ДЕЛЕГАТЫ (для всех источников разом) и мёртвые
    // ИСТОЧНИКИ (для обоих делегатов). См. комментарий у ensure(): аборт на
    // пробе — про машину, отказ загрузки — про адрес, и повторять их поперёк
    // бессмысленно. Жалоба Левона 06.09: шесть кандидатов × (загрузка +
    // создание + аборт) ≈ 54 с тяжёлой работы на слабой машине — «тормозит».
    var _deadDelegate = {}, _deadSource = {};
    // Последние строки консоли во время подъёма кандидата: Emscripten и TFLite
    // пишут причину аборта в console.error ДО того, как бросить голое
    // «Aborted()». Без этого телеметрия видела только «Aborted()» и причину
    // приходилось воспроизводить наугад.
    var _conTail = [];
    function captureConsole(on) {
        try {
            if (on) {
                _conTail = [];
                if (!console.__frOrig) {
                    console.__frOrig = { error: console.error, warn: console.warn };
                    var tap = function (kind) {
                        return function () {
                            try {
                                var line = Array.prototype.map.call(arguments, function (a) { return typeof a === 'string' ? a : (a && a.message) || String(a); }).join(' ');
                                if (!/^\[face-retouch\]/.test(line)) { _conTail.push(kind + ': ' + line.slice(0, 160)); if (_conTail.length > 5) _conTail.shift(); }
                            } catch (_) { }
                            return console.__frOrig[kind].apply(console, arguments);
                        };
                    };
                    console.error = tap('error'); console.warn = tap('warn');
                }
            } else if (console.__frOrig) {
                console.error = console.__frOrig.error; console.warn = console.__frOrig.warn;
                console.__frOrig = null;
            }
        } catch (_) { }
    }
    // Скачанные байты модели живут ОТДЕЛЬНО от детектора: смена режима
    // (IMAGE ↔ VIDEO) пересоздаёт детектор, и без этого кэша те же ~3.7 МБ
    // качались бы заново — на телефоне это лишние секунды тишины.
    var _modelBytes = null, _modelFrom = null;
    // Подписчики на ход загрузки. Их может быть несколько (страница показывает
    // полосу, предзагрузка молчит), поэтому это список, а не одна функция.
    var _watchers = [];
    var _phase = null;   // 'esm' | 'model' | 'init' | 'ready' | 'failed'
    var _got = 0, _total = 0;

    /**
     * Сообщить о ходе загрузки. Три фазы, и только у одной есть проценты:
     * библиотека и сборка детектора на GPU своей длины не знают — врать про
     * них процентами хуже, чем честно сказать «готовлю».
     */
    function emit(phase, got, total) {
        _phase = phase;
        if (got !== undefined) _got = got;
        if (total !== undefined) _total = total;
        for (var i = 0; i < _watchers.length; i++) {
            try { _watchers[i]({ phase: _phase, loaded: _got, total: _total }); } catch (_) { }
        }
    }

    /**
     * Подписаться на ход загрузки. Возвращает функцию отписки и СРАЗУ отдаёт
     * текущее состояние: подписчик, пришедший в середине загрузки (страница
     * успела отрисоваться позже), иначе увидел бы пустую полосу до следующего
     * куска — то есть решил бы, что всё зависло, ровно в тот момент, когда
     * полоса и заводится ради обратного.
     */
    function onProgress(fn) {
        if (typeof fn !== 'function') return function () { };
        _watchers.push(fn);
        if (_phase) { try { fn({ phase: _phase, loaded: _got, total: _total }); } catch (_) { } }
        return function () {
            var i = _watchers.indexOf(fn);
            if (i >= 0) _watchers.splice(i, 1);
        };
    }

    /**
     * Скачать модель САМИМ, а не отдать адрес MediaPipe.
     *
     * Причина одна: `modelAssetPath` качает файл внутри wasm-обвязки, и ход
     * загрузки оттуда не виден никак — на медленном канале страница просто
     * молчит десяток секунд. Тот же файл, скачанный явно и переданный как
     * `modelAssetBuffer`, даёт честные байты и, главное, ПРИЧИНУ отказа
     * (404/CORS/обрыв) вместо общего «не удалось создать детектор».
     *
     * ⚠️ Качает ОБЩИЙ БЛОК `__mdFetchBytes`, а не свой `fetch` с ридером, и
     * это не вкусовщина. Свой однопроходный стриминг здесь уже был написан и
     * снят ModelDownloadResumeTest: обрыв мобильного канала — норма (замер:
     * 173 сессии за 30 дней умерли на загрузке модели, обрывы на РАЗНЫХ
     * позициях), а однопроходный загрузчик его не переживает. Общий блок
     * умеет докачку по Range, сторожа молчания и — что не менее важно —
     * кладёт собранные байты в кэш моделей: 3.6 МБ качаются один раз на
     * устройство, а не на каждый визит.
     *
     * Fail-open: любая осечка бросает, и вызывающий уходит на
     * `modelAssetPath` — прогресс важен, но не важнее работоспособности.
     */
    var _dlReady = null;
    function loadDownloader(url) {
        if (root.__mdFetchBytes) return Promise.resolve(root.__mdFetchBytes);
        if (_dlReady) return _dlReady;
        _dlReady = new Promise(function (resolve, reject) {
            var s = document.createElement('script');
            // Адрес приходит от страницы: в статике нет доступа к роутеру
            // версий. ⚠️ И запасной путь обязан идти через `__jsSrc`: голый
            // `/js/…` отдаётся с `immutable` на 180 дней, то есть у того, у
            // кого файл уже есть, правка не появится НИКОГДА — а у тебя всё
            // выглядит исправно, потому что ты грузишь его впервые.
            s.src = url || (root.__jsSrc ? root.__jsSrc('/js/model-download.js') : '/js/model-download.js');
            s.onload = function () {
                root.__mdFetchBytes
                    ? resolve(root.__mdFetchBytes)
                    : reject(new Error('model-download loaded but __mdFetchBytes missing'));
            };
            s.onerror = function () { _dlReady = null; reject(new Error('model-download.js failed to load')); };
            document.head.appendChild(s);
        });
        return _dlReady;
    }

    async function fetchModel(url, opts) {
        if (_modelBytes && _modelFrom === url) { emit('model', _modelBytes.length, _modelBytes.length); return _modelBytes; }
        var fetchBytes = await loadDownloader(opts && opts.downloaderUrl);
        emit('model', 0, 0);
        var out = await fetchBytes(url, {
            // total = 0, когда размер неизвестен (сжатие транспортом) — UI
            // сам решает показывать ли проценты, здесь мы не выдумываем.
            onProgress: function (received, total) { emit('model', received, total); },
            signal: opts && opts.signal,
        });
        _modelBytes = out; _modelFrom = url;
        return out;
    }

    /**
     * ПОРЯДОК ДЕЛЕГАТОВ — CPU ПЕРВЫМ. Это замер, а не вкус (06.09.2026).
     *
     * Детектор здесь смотрит на кадр 300–380 px (тулы уменьшают его сами),
     * и на таком входе GPU-делегат не выигрывает ничего:
     *
     *   RTX 4060 (ANGLE/D3D11), кадр 300 px:  GPU 26.5 мс · CPU 25.7 мс
     *   SwiftShader (программный GL):          GPU 77–86 мс · CPU 14 мс (simd) / 50 мс (nosimd)
     *
     * Зато падает именно GPU-делегат. Жалоба Левона 05.09 (Yandex 26, Win10,
     * 4 ГБ, процессор без wasm-SIMD, WebGPU нет): детектор СОЗДАВАЛСЯ, а на
     * первом же `detect` wasm бросал «Aborted()». Воспроизведено эмуляцией
     * калечного WebGL2 — контекст есть, но без float-текстур и с текстурой
     * 2048 (старые Intel/AMD): GPU-делегат создаётся и падает на первом выводе,
     * CPU-делегат на той же машине работает. Это известное поведение
     * MediaPipe (issue #6296: RET_CHECK внутри графа при нехватке WebGL-
     * возможностей → abort вместо ошибки). Аборты видны и у /video-blur на
     * машинах С SIMD и WebGPU (chrome/safari/yandex) — то есть дело не в
     * старом процессоре, а в GPU-пути как таковом.
     *
     * GPU остаётся вторым кандидатом на случай, если CPU-путь не создался,
     * — не ради скорости. Источник может ПРИКОЛОТЬ делегат (`delegate:` в
     * записи), тогда для него пробуется только он.
     */
    /* Лейн = делегат MediaPipe + то, ЧЕМ он получает картинку. «CPU+soft» —
       тот же CPU-делегат, но вход идёт через программный «GL» из
       mediapipe-soft-gl.js, а не через WebGL браузера (см. ⛔ у run()).
       Без WebGL2 он первый: настоящий CPU там падает всегда (в поле — каждый
       раз, `gl2:0`), а прокладке GL не нужен вовсе. С WebGL2 — страховочная
       ступень после настоящего CPU (Safari «Error querying for GL extensions»). */
    function lanes(pinned) {
        var gl = glSummary();
        var all = gl.gl2 ? ['CPU', 'CPU+soft', 'GPU'] : ['CPU+soft', 'CPU', 'GPU'];
        if (!pinned) return all;
        return all.filter(function (l) { return l.split('+')[0] === pinned; });
    }

    function candidates(sources) {
        var out = [];
        for (var i = 0; i < sources.length; i++) {
            var src = sources[i];
            var ls = lanes(src.delegate);
            for (var d = 0; d < ls.length; d++) {
                var via = src.via || ('src' + i);
                out.push({ src: src, lane: ls[d], delegate: ls[d].split('+')[0], soft: ls[d] === 'CPU+soft',
                           via: via, index: i, key: via + '/' + ls[d] });
            }
        }
        return out;
    }

    /* Прокладка грузится только тем, кому она нужна, и через `__jsSrc`:
       голый /js/… отдаётся immutable на 180 дней. */
    var _softReady = null;
    function loadSoftGL() {
        if (root.MediaPipeSoftGL) return Promise.resolve(root.MediaPipeSoftGL);
        if (_softReady) return _softReady;
        _softReady = new Promise(function (resolve, reject) {
            var s = document.createElement('script');
            s.src = root.__jsSrc ? root.__jsSrc('/js/mediapipe-soft-gl.js') : '/js/mediapipe-soft-gl.js';
            s.onload = function () {
                root.MediaPipeSoftGL ? resolve(root.MediaPipeSoftGL)
                    : reject(new Error('mediapipe-soft-gl loaded but MediaPipeSoftGL missing'));
            };
            s.onerror = function () { _softReady = null; reject(new Error('mediapipe-soft-gl.js failed to load')); };
            document.head.appendChild(s);
        });
        return _softReady;
    }

    /**
     * Сводка WebGL для телеметрии — ОДИН раз на страницу. Без неё отказ
     * GPU-пути в телеметрии выглядел как «Aborted()» и ничего больше: по
     * жалобе Левона у нас не было ни рендерера, ни того, есть ли WebGL2, и
     * причину пришлось воспроизводить наугад. Три поля закрывают это.
     */
    var _gl = null;
    function glSummary() {
        if (_gl) return _gl;
        var o = { gl2: 0, gl1: 0, flt: 0, r: '' };
        try {
            var c = document.createElement('canvas');
            var g = c.getContext('webgl2');
            if (!g) {
                // Только WebGL1 — когорта, где MediaPipe падает и на CPU (см. ⛔ выше).
                var c1 = document.createElement('canvas');
                var g1 = c1.getContext('webgl');
                if (g1) { o.gl1 = 1; var l1 = g1.getExtension('WEBGL_lose_context'); if (l1) l1.loseContext(); }
            }
            if (g) {
                o.gl2 = 1;
                o.flt = g.getExtension('EXT_color_buffer_float') ? 1 : 0;
                var dbg = g.getExtension('WEBGL_debug_renderer_info');
                o.r = String(dbg ? g.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : g.getParameter(g.RENDERER)).slice(0, 48);
                var lose = g.getExtension('WEBGL_lose_context'); if (lose) lose.loseContext();
            }
        } catch (_) { }
        _gl = o;
        return o;
    }

    /**
     * ПРОБА: один настоящий вывод на крошечном холсте сразу после создания.
     *
     * ⚠️⚠️ «Создался» ≠ «работает». MediaPipe поднимает GPU-граф лениво, на
     * ПЕРВОМ выводе, и именно там падает калечный WebGL — createFromOptions
     * при этом отрабатывает без единой жалобы. Лестница, которая проверяет
     * кандидата только созданием, слепа к этому по построению: она честно
     * отдаёт «готовый» детектор, а он умирает на первом кадре у человека.
     * Цена пробы — один вывод на 96×96 (≈15–50 мс), платится один раз.
     */
    var _probe = null;
    function probeCanvas() {
        if (!_probe) {
            _probe = document.createElement('canvas');
            _probe.width = 96; _probe.height = 96;
            var x = _probe.getContext('2d');
            x.fillStyle = '#888'; x.fillRect(0, 0, 96, 96);
        }
        return _probe;
    }
    function probe(det, mode) {
        var c = probeCanvas();
        if (mode === 'VIDEO') { _ts = _ts + 1; det.detectForVideo(c, _ts); }
        else det.detect(c);
    }

    /** Ошибка, которая означает «wasm-модуль мёртв», а не «плохой вход». */
    function isWasmDeath(e) {
        if (!e) return false;
        if (typeof WebAssembly !== 'undefined' && WebAssembly.RuntimeError && e instanceof WebAssembly.RuntimeError) return true;
        var m = String((e && (e.message || e.type)) || e);
        return /Aborted|abort\(|out of bounds|unreachable|RET_CHECK|CalculatorGraph|INTERNAL:/i.test(m)
            || e.name === 'RuntimeError';
    }

    function unavailable(cause) {
        var err = new Error((cause && cause.message) ? cause.message : 'face-landmarker-unavailable');
        err.name = 'FRDetectorUnavailable';
        err.cause = cause || null;
        return err;
    }

    /** Поднять ОДНОГО кандидата: файлы → модель → создание → проба. */
    async function raise(cand, mode, opts) {
        var stage = 'load';
        emit('esm', 0, 0);
        var mp = await import(/* webpackIgnore: true */ cand.src.esm);
        var fileset = await mp.FilesetResolver.forVisionTasks(cand.src.wasm);
        // Байты модели тянем сами — ради полосы и ради внятной причины
        // отказа. Не вышло — отдаём адрес, как раньше.
        var bytes = null;
        try { bytes = await fetchModel(cand.src.model, { downloaderUrl: opts.downloaderUrl }); }
        catch (me) {
            try { console.warn('[face-retouch] модель не скачалась напрямую:', cand.src.model, me && me.message); } catch (_) { }
        }
        emit('init');
        var softCanvas;
        if (cand.soft) {
            stage = 'softgl';
            try { softCanvas = (await loadSoftGL()).canvas(); }
            catch (se) { se.frStage = stage; throw se; }
        }
        stage = 'create';
        var det;
        try {
            det = await mp.FaceLandmarker.createFromOptions(fileset, {
                baseOptions: bytes
                    ? { modelAssetBuffer: bytes, delegate: cand.delegate }
                    : { modelAssetPath: cand.src.model, delegate: cand.delegate },
                // undefined — MediaPipe берёт свой OffscreenCanvas с настоящим GL.
                canvas: softCanvas,
                runningMode: mode,
                numFaces: opts.numFaces || 4,
                // Радужка нужна не для красоты: контур глаза без неё грубее,
                // а мы этим контуром ЗАЩИЩАЕМ глаз от снятия блика — промах
                // здесь виден сразу.
                // Блендшейпы (моргание, eyeLook*) и матрица позы головы нужны
                // исправлению взгляда (/eye-contact); ретушь их не просит —
                // модель блендшейпов стоит лишний вывод на каждое лицо.
                outputFaceBlendshapes: !!opts.blendshapes,
                outputFacialTransformationMatrixes: !!opts.matrix,
                minFaceDetectionConfidence: 0.4,
                minFacePresenceConfidence: 0.4,
                minTrackingConfidence: 0.4,
            });
        } catch (e) { e.frStage = stage; throw e; }
        stage = 'probe';
        var t0 = (typeof performance !== 'undefined' ? performance : Date).now();
        try { probe(det, mode); }
        catch (e) {
            try { det.close && det.close(); } catch (_) { }
            e.frStage = stage; throw e;
        }
        return { det: det, mp: mp, bytes: bytes ? bytes.length : 0, softCanvas: softCanvas || null,
                 probeMs: Math.round((typeof performance !== 'undefined' ? performance : Date).now() - t0) };
    }

    /**
     * Загрузка детектора. Кандидаты (источник × делегат) пробуются по
     * очереди, и каждый проверяется САМИМ ВЫВОДОМ (см. probe): forVisionTasks()
     * резолвится и на мёртвом адресе, createFromOptions() отрабатывает и на
     * калечном GPU — честная проба только вывод. Тот же урок, что в video-blur
     * и subject-tracker, доведённый до конца.
     */
    function ensure(sources, opts) {
        opts = opts || {};
        var mode = opts.mode || 'IMAGE';
        if (_det && _mode === mode) return Promise.resolve(_det);
        if (_ready && _mode === mode) return _ready;
        // Режим сменился — прежний детектор надо ЗАКРЫТЬ, а не бросить.
        // Его wasm-куча живёт на главном потоке и обратно уже не отдаётся
        // (замер: даже принудительный gc() не возвращает ни байта), поэтому
        // брошенный детектор удерживается до конца жизни вкладки.
        if (_det) close();
        _mode = mode; _sources = sources; _opts = opts;
        _ready = (async function () {
            var lastErr = null;
            var t0 = (typeof performance !== 'undefined' ? performance : Date).now();
            var list = candidates(sources);
            var tried = 0;
            for (var i = 0; i < list.length; i++) {
                var cand = list[i];
                if (_dead[cand.key] || _deadDelegate[cand.lane] || _deadSource[cand.via]) continue;
                tried++;
                try {
                    captureConsole(true);
                    var r = await raise(cand, mode, opts);
                    captureConsole(false);
                    _det = r.det; _mp = r.mp; _cand = cand;
                    // КАКОЙ источник сработал — единственный способ узнать, что
                    // наша собственная раздача отказала у живых людей. Без него
                    // отказ /vendor/ выглядит как «просто медленнее»: цепочка
                    // молча уходит на внешний адрес, а для российского трафика
                    // именно он и может быть недоступен. Делегат, сборка и
                    // сводка GL — чтобы следующий Левон читался одним запросом.
                    _source = { via: cand.via, delegate: cand.delegate, lane: cand.lane, index: cand.index, tried: tried,
                                ms: Math.round((typeof performance !== 'undefined' ? performance : Date).now() - t0),
                                bytes: r.bytes, probeMs: r.probeMs };
                    emit('ready');
                    try {
                        var gl = glSummary();
                        // Прокладка обязана перечислять вызовы GL, которых не
                        // исполняет: новая версия MediaPipe, позвавшая что-то
                        // сверх замеренного набора, видна здесь, а не тихо.
                        var sst = r.softCanvas && root.MediaPipeSoftGL ? root.MediaPipeSoftGL.stats(r.softCanvas) : null;
                        if (root.__ae) root.__ae('feature_use', {
                            f: 'fr_model_source', via: cand.via, delegate: cand.delegate, lane: cand.lane, tried: tried, ms: _source.ms,
                            probeMs: r.probeMs, nosimd: root.__noSimd ? 1 : 0, gl2: gl.gl2, gl1: gl.gl1, flt: gl.flt, gpu: gl.r,
                            sglUnknown: sst ? Object.keys(sst.unknown).join(',').slice(0, 120) : undefined,
                        });
                    } catch (_) { }
                    return _det;
                } catch (e) {
                    var tail = _conTail.slice();
                    captureConsole(false);
                    lastErr = e;
                    var stage = (e && e.frStage) || 'load';
                    // ⚠️ Кого гасить — решает СТУПЕНЬ отказа, и это не вкус:
                    //  · проба — умер wasm. Он ОДИН И ТОТ ЖЕ у всех источников
                    //    (они различаются только адресом), значит этот делегат
                    //    мёртв везде; пробовать его с прокси и с CDN — это ещё
                    //    два скачивания, два создания и два аборта впустую;
                    //  · загрузка — не доехал ESM или модель. Второй делегат
                    //    того же адреса упрётся в тот же 404; а другой адрес —
                    //    имеет право сработать, канал и CDN — разные.
                    //  · прокладка не загрузилась — это про неё, не про адрес и не
                    //    про делегат: гасим один лейн, настоящий CPU ещё в очереди.
                    if (stage === 'probe' || stage === 'softgl') { _dead[cand.key] = stage; _deadDelegate[cand.lane] = stage; }
                    else if (stage === 'load') { _deadSource[cand.via] = stage; }
                    try {
                        var gl2 = glSummary();
                        if (root.__ae) root.__ae('tool_diagnostic', {
                            issue: 'fr_source_failed', via: cand.via, delegate: cand.delegate, lane: cand.lane, stage: stage,
                            nosimd: root.__noSimd ? 1 : 0, gl2: gl2.gl2, gl1: gl2.gl1, flt: gl2.flt, gpu: gl2.r,
                            msg: String((e && (e.message || e.type)) || e).slice(0, 120),
                            log: tail.join(' ‖ ').slice(0, 600),
                        });
                    } catch (_) { }
                    try { console.warn('[face-retouch] кандидат не подошёл (' + stage + '):', cand.key, e && (e.message || e.type)); } catch (_) { }
                }
            }
            _ready = null;   // дать следующему вызову шанс после сетевого сбоя
            emit('failed');
            throw unavailable(lastErr);
        })();
        return _ready;
    }

    /** Связи MediaPipe → ВСЕ замкнутые кольца индексов (их бывает больше одного). */
    function ringsFrom(connections) {
        if (!connections || !connections.length) return [];
        var next = new Map(), starts = [];
        for (var i = 0; i < connections.length; i++) {
            var c = connections[i];
            var a = (c.start !== undefined) ? c.start : c[0];
            var b = (c.end !== undefined) ? c.end : c[1];
            if (!next.has(a)) { next.set(a, b); starts.push(a); }
        }
        var seen = new Set(), out = [];
        for (var s = 0; s < starts.length; s++) {
            var st = starts[s];
            if (seen.has(st)) continue;
            var ring = [st], cur = st, guard = 0;
            seen.add(st);
            while (guard++ < 600) {
                var nx = next.get(cur);
                if (nx === undefined || nx === st) break;
                if (seen.has(nx)) break;
                seen.add(nx); ring.push(nx); cur = nx;
            }
            if (ring.length > 4) out.push(ring);
        }
        return out;
    }

    /** Одно кольцо — для контуров, у которых оно одно (совместимость). */
    function ringFrom(connections) {
        var r = ringsFrom(connections);
        return r.length ? r[0] : null;
    }

    /**
     * ВНУТРЕННИЙ контур губ — то самое отверстие рта, внутри которого зубы.
     *
     * Индексы заданы явно, и это осознанно. Вывести кольцо обходом графа
     * связей FACE_LANDMARKS_LIPS не получается: набор содержит ДВА контура с
     * общими узлами, обход отдаёт одно полное кольцо и обрывок второго, и по
     * площади выбирается обрывок. На реальном снимке это выглядело как узкая
     * полоска у кромки зубов — маска ложилась вдоль десны, а не на зубы.
     * Топология FaceMesh/FaceLandmarker (468/478 точек) стабильна и
     * задокументирована, поэтому индексы здесь надёжнее вывода.
     */
    var INNER_LIP = [78, 191, 80, 81, 82, 13, 312, 311, 310, 415,
                     308, 324, 318, 402, 317, 14, 87, 178, 88, 95];

    /**
     * ВНЕШНИЙ контур губ — по той же причине явными индексами (25.09.2026).
     *
     * До этого «губы» брались первым кольцом обхода того же графа связей, и
     * это был ОБРЫВОК: нижняя дуга губы, замкнутая хордой через уголки рта
     * (обход отдаёт четыре куска по 9–11 точек). Им защищалась кожа от
     * снятия блика и «покраснения» — значит, верхняя губа не защищалась
     * вовсе. Замер на пресете «сильно» по семи лицам из живых интервью:
     * верхняя губа менялась на ΔE 2.6…9.8 (насыщенность −2…−7, у красной
     * помады сильнее всего), нижняя — 0.0. Губа бледнела наполовину, и никто
     * этого не видел: числа по «губам» считались по тому же обрывку.
     */
    var OUTER_LIP = [61, 185, 40, 39, 37, 0, 267, 269, 270, 409,
                     291, 375, 321, 405, 314, 17, 84, 181, 91, 146];

    /**
     * Глаза и брови — тоже явными индексами (25.09.2026, та же находка).
     * Обход FACE_LANDMARKS_{LEFT,RIGHT}_EYE отдавал НИЖНЕЕ ВЕКО (9 точек),
     * замкнутое хордой через уголки, а ..._EYEBROW — нижний край брови (5
     * точек). Следствия, замеренные на живых интервью: защита глаза от ретуши
     * кожи покрывала нижнюю половину глаза (радужка с бликом и верхнее веко
     * получали ΔE до 1.9 на «сильно»), а «белок глаз» осветлял клин под глазом —
     * ΔL* белка +0.3 на 100 %, ни одного кадра с заметным эффектом.
     * Порядок точек глаза: от внешнего уголка по нижнему веку к внутреннему
     * (первые 9 — ровно прежнее нижнее веко: на него опирается полоса «под
     * глазами»), дальше по верхнему веку обратно. Радужка — 468–472 (правая),
     * 473–477 (левая): центр и четыре точки края.
     */
    var RIGHT_EYE = [33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160, 161, 246];
    var LEFT_EYE = [263, 249, 390, 373, 374, 380, 381, 382, 362, 398, 384, 385, 386, 387, 388, 466];
    var RIGHT_BROW = [46, 53, 52, 65, 55, 107, 66, 105, 63, 70];
    var LEFT_BROW = [276, 283, 282, 295, 285, 336, 296, 334, 293, 300];
    var RIGHT_IRIS = [468, 469, 470, 471, 472];
    var LEFT_IRIS = [473, 474, 475, 476, 477];

    var _rings = null;
    function rings() {
        if (_rings || !_mp) return _rings;
        var F = _mp.FaceLandmarker;
        // ⚠️⚠️ У ГУБ КОЛЕЦ ДВА: внешний край и внутренний (отверстие рта), и
        // ОБА заданы явными индексами (OUTER_LIP / INNER_LIP): обход графа
        // FACE_LANDMARKS_LIPS отдаёт не кольца, а обрывки, и первый из них —
        // лишь нижняя дуга губы.
        _rings = {
            oval: ringFrom(F.FACE_LANDMARKS_FACE_OVAL),
            leftEye: LEFT_EYE,
            rightEye: RIGHT_EYE,
            lips: OUTER_LIP,
            leftBrow: LEFT_BROW,
            rightBrow: RIGHT_BROW,
        };
        return _rings;
    }

    /** Площадь многоугольника (знак не важен — берём модуль). */
    function polyArea(pg) {
        if (!pg || pg.length < 3) return 0;
        var a = 0;
        for (var i = 0, n = pg.length; i < n; i++) {
            var p = pg[i], q = pg[(i + 1) % n];
            a += p[0] * q[1] - q[0] * p[1];
        }
        return a / 2;
    }

    function poly(pts, ring) {
        if (!ring) return null;
        var out = new Array(ring.length);
        for (var i = 0; i < ring.length; i++) {
            var p = pts[ring[i]];
            out[i] = p ? [p.x, p.y] : [0, 0];
        }
        return out;
    }

    /**
     * ⛔ ГРАНИЦА, А НЕ НЕДОДЕЛКА (06.09.2026, разбор машины Левона).
     *
     * На машине БЕЗ WebGL2, но С WebGL1 (старая Intel; в его телеметрии
     * `gl2:0`) MediaPipe 0.10.34 падает и на CPU-делегате: C++-сторона берёт
     * WebGL1 для GlTextureBuffer и разбивается о свой CHECK —
     * «gl_texture_buffer.cc:239 Check failed: prod_token → Aborted()» (issue
     * #4855). Воспроизведено эмуляцией «WebGL2 нет, WebGL1 есть».
     * Тогда проверили и отвергли три обхода (холст без GL, ImageData на вход,
     * спрятанные конструкторы) и записали «граница, а не недоделка».
     *
     * ✅ 27.09.2026 — ГРАНИЦА СНЯТА: лейн «CPU+soft» (mediapipe-soft-gl.js).
     * Замер вызовов GL на CPU-делегате показал, что MediaPipe нужен не GL, а
     * одна операция — залить кадр текстурой и прочитать его обратно
     * (texImage2D → framebufferTexture2D → readPixels + fenceSync); ни шейдеров,
     * ни отрисовки. Программный «WebGL2», исполняющий ровно это в памяти,
     * отдаётся через options.canvas. 9 настоящих лиц, эталон — настоящий GL:
     *   прокладка при живом GL      9/9, Δ 0,000 px (побитово), ~132 мс против 106
     *   прокладка, только WebGL1    9/9, Δ 0,000 px   ← машина Левона, Яндекс
     *   прокладка, GL нет вовсе     9/9, Δ 0,000 px   ← Chrome/Safari без GL
     *   настоящий GL, только WebGL1 0/9, Aborted()   ← прежнее поведение
     * ⚠️ Прежняя запись «без GL вовсе CPU работает (флаг браузера)» — ошибка
     * прибора: `--disable-webgl(2)` гасит GL только у <canvas>, а MediaPipe
     * рисует на OffscreenCanvas, где WebGL2 остаётся. В поле без GL вовсе
     * (gl1:0) CPU падает TypeError'ом. Честная эмуляция — getContext('webgl2')
     * → null у ОБОИХ типов холстов (стенд tests/playwright/face-detector-without-webgl2.mjs).
     * RU за 30 дней до правки: /passport-photo ~25 сессий, /video-retouch 3,
     * /face-retouch 1–2 без детектора на любом делегате.
     */
    /** Сырой вызов детектора в нужном режиме — с монотонной меткой времени. */
    function run(source, opts) {
        // VIDEO-режим требует СТРОГО неубывающих меток времени, и нарушение
        // роняет весь детектор с исключением из wasm. Счётчик держим ЗДЕСЬ, а
        // не у вызывающих: у ролика несколько независимых проходов (разбор
        // сцены, предпросмотр, экспорт), каждый начинает своё время с нуля, и
        // достаточно одного, чтобы уронить остальные. Инвариант обязан жить в
        // примитиве, а не в договорённости между вызывающими.
        if (_mode === 'VIDEO') {
            var t = Math.max(_ts + 1, Math.round(opts.timestamp || 0));
            _ts = t;
            return _det.detectForVideo(source, t);
        }
        return _det.detect(source);
    }

    /**
     * Найти лица. Координаты возвращаются НОРМАЛИЗОВАННЫМИ 0..1 — тул может
     * менять масштаб превью, не пересчитывая геометрию.
     *
     * ⚠️ Смерть wasm ПОСРЕДИ работы лечится здесь, а не у вызывающих. Проба
     * при создании ловит калечный GPU на первом выводе, но модуль может
     * умереть и позже (нехватка памяти, потерянный GL-контекст). Тогда
     * детектор закрывается, кандидат помечается мёртвым, поднимается
     * следующий — и ТОТ ЖЕ вызов повторяется один раз. Вызывающий видит либо
     * результат, либо FRDetectorUnavailable, когда кандидатов не осталось.
     */
    async function detect(source, srcW, srcH, opts) {
        if (!_det) throw new Error('detector not ready');
        opts = opts || {};
        var res;
        try {
            res = run(source, opts);
        } catch (e) {
            await recover(e);               // следующий кандидат — или бросок дальше
            res = run(source, opts);
        }
        return toFaces(res);
    }

    /**
     * То же, но СИНХРОННО и без лечения: смерть wasm вылетает наружу как есть.
     * Нужен вызывающим, которые сами стоят внутри синхронного обратного
     * вызова (кадровый проход конвейера WebCodecs: draw() там не может ждать),
     * — они ловят ошибку, зовут recover() снаружи и повторяют кадр.
     * Сам детектор MediaPipe синхронный; асинхронность detect() — это только
     * лечение.
     */
    function detectSync(source, srcW, srcH, opts) {
        if (!_det) throw new Error('detector not ready');
        return toFaces(run(source, opts || {}));
    }

    /**
     * Вылечить смерть wasm ПОСРЕДИ работы: закрыть мёртвого кандидата, пометить
     * его делегат мёртвым у всех источников, поднять следующего. Бросает ту же
     * ошибку, если это не смерть wasm, и FRDetectorUnavailable, когда кандидатов
     * не осталось. Одна реализация на detect() и на синхронных вызывающих.
     */
    async function recover(e) {
        if (!isWasmDeath(e) || !_cand || !_sources) throw e;
        var died = _cand;
        _dead[died.key] = 'run';
        _deadDelegate[died.lane] = 'run';   // тот же wasm у всех источников
        try {
            if (root.__ae) root.__ae('tool_diagnostic', {
                issue: 'fr_detector_died', via: died.via, delegate: died.delegate, lane: died.lane,
                msg: String((e && (e.message || e.type)) || e).slice(0, 120),
            });
        } catch (_) { }
        var srcs = _sources, o = _opts;
        close();
        await ensure(srcs, o);          // следующий кандидат — или FRDetectorUnavailable
        try {
            if (root.__ae) root.__ae('feature_use', {
                f: 'fr_detector_recovered', from: died.key, to: _cand ? _cand.key : '',
            });
        } catch (_) { }
    }

    /** Сырой результат MediaPipe → лица с рамкой и контурами (координаты 0..1). */
    function toFaces(res) {
        var faces = [];
        var list = (res && res.faceLandmarks) || [];
        var bsl = (res && res.faceBlendshapes) || [];
        var mtx = (res && res.facialTransformationMatrixes) || [];
        var R = rings();
        for (var i = 0; i < list.length; i++) {
            var pts = list[i];
            var minX = 1, minY = 1, maxX = 0, maxY = 0;
            for (var j = 0; j < pts.length; j++) {
                if (pts[j].x < minX) minX = pts[j].x;
                if (pts[j].y < minY) minY = pts[j].y;
                if (pts[j].x > maxX) maxX = pts[j].x;
                if (pts[j].y > maxY) maxY = pts[j].y;
            }
            faces.push({
                pts: pts,
                box: { x: minX, y: minY, w: maxX - minX, h: maxY - minY },
                oval: poly(pts, R && R.oval),
                leftEye: poly(pts, R && R.leftEye),
                rightEye: poly(pts, R && R.rightEye),
                // Внешний контур губ и ОТВЕРСТИЕ рта. Для зубов нужны оба:
                // отверстие — где искать, кольцо между ними (сама губа) —
                // эталон, с которым сравнивается содержимое рта.
                lips: poly(pts, OUTER_LIP),
                mouthOpen: poly(pts, INNER_LIP),
                leftBrow: poly(pts, R && R.leftBrow),
                rightBrow: poly(pts, R && R.rightBrow),
            });
            // Только если попросили при ensure({blendshapes, matrix}).
            if (bsl[i] && bsl[i].categories) {
                var bs = {};
                for (var c = 0; c < bsl[i].categories.length; c++) bs[bsl[i].categories[c].categoryName] = bsl[i].categories[c].score;
                faces[faces.length - 1].bs = bs;
            }
            if (mtx[i] && mtx[i].data) faces[faces.length - 1].m = Array.prototype.slice.call(mtx[i].data);
        }
        return faces;
    }

    function close() {
        try { if (_det && _det.close) _det.close(); } catch (_) { }
        _det = null; _ready = null; _mode = null; _ts = 0; _source = null; _cand = null;
        // `_sources` / `_opts` / `_dead` остаются: это память лестницы, по
        // которой detect() поднимает следующего кандидата после смерти wasm.
    }

    /**
     * Прогреть модель ЗАРАНЕЕ — до того, как файл выбран.
     *
     * Замер жалобы, с которой это заводилось: между выбором снимка и первым
     * результатом проходило 6–21 с, и всё это время страница молчала. Полоса
     * загрузки честно показывает ожидание, но лучшее ожидание — то, которого
     * нет: к моменту выбора файла модель уже в памяти.
     *
     * Что здесь НЕ делается: безусловная качка 3.7 МБ у каждого, кто открыл
     * страницу. Прогрев начинается по первому ПРИЗНАКУ НАМЕРЕНИЯ (наведение
     * или фокус на зоне загрузки, перетаскивание файла) — их вешает страница,
     * — а сам по себе только на быстром безлимитном канале.
     */
    function warm(sources, opts) {
        if (_det || _ready) return;
        try {
            var c = root.navigator && root.navigator.connection;
            if (c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || ''))) return;
        } catch (_) { }
        try { ensure(sources, opts).catch(function () { }); } catch (_) { }
    }

    root.FRLandmarks = {
        ensure: ensure, warm: warm, onProgress: onProgress, detect: detect, detectSync: detectSync, recover: recover, close: close,
        isWasmDeath: isWasmDeath,
        rings: rings, ringFrom: ringFrom, ringsFrom: ringsFrom, polyArea: polyArea,
        /** Готов ли детектор ПРЯМО СЕЙЧАС — без обещания его загрузить. */
        loaded: function () { return !!_det; },
        phase: function () { return _phase; },
        /** Какой источник сработал и за сколько — для телеметрии и отладки. */
        source: function () { return _source; },
        /** Имя ошибки, которую бросают ensure()/detect(), когда кандидатов не осталось. */
        /** Кольца губ — видео пересобирает контуры из сглаженных точек. */
        INNER_LIP: INNER_LIP,
        OUTER_LIP: OUTER_LIP,
        LEFT_EYE: LEFT_EYE, RIGHT_EYE: RIGHT_EYE, LEFT_BROW: LEFT_BROW, RIGHT_BROW: RIGHT_BROW,
        LEFT_IRIS: LEFT_IRIS, RIGHT_IRIS: RIGHT_IRIS,
        UNAVAILABLE: 'FRDetectorUnavailable',
        isUnavailable: function (e) { return !!e && e.name === 'FRDetectorUnavailable'; },
    };
})(typeof window !== 'undefined' ? window : globalThis);
